import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Collapse, IconButton, Stack, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import BlacklistItemMenu from './blacklist-item-menu';
import BlacklistItemTable from './blacklist-item-table';
import { Blacklist_New, BlacklistTypes } from './company-blacklist.types';

interface ItemProps {
  blacklist: Blacklist_New;
  loading: boolean;

  handleOpenEditBlacklistItemsDialog(): void;

  handleOpenEditBlacklistNameDialog(): void;

  handleOpenDeleteBlacklistDialog(): void;

  handleRemoveSingleBlacklistItem(identifier: string): () => void;
}

const BlacklistItem: React.FC<ItemProps> = ({
  blacklist,
  loading,
  handleOpenEditBlacklistItemsDialog,
  handleOpenEditBlacklistNameDialog,
  handleOpenDeleteBlacklistDialog,
  handleRemoveSingleBlacklistItem,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState(false);

  const handleToggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  useEffect(() => {
    if (_.isEmpty(blacklist.items)) {
      setExpanded(false);
    }
  }, [blacklist.items]);

  const [anchorEl, setAnchorEl] = useState<any | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleOpenMenu = (el: any) => {
    setMenuOpen(true);
    setAnchorEl(el);
  };

  const handleCloseMenu = () => {
    setMenuOpen(false);
    setAnchorEl(null);
  };

  return (
    <div className={classes.blacklistItem}>
      <Stack direction={'row'} gap={1}>
        <div className={classes.blacklistItemContent}>
          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
            <Stack direction={'row'} gap={1}>
              <Typography className={classes.regularText}>{_.truncate(blacklist.name || '...', { length: 35 })}</Typography>
              <Stack direction={'row'} alignItems={'center'} gap={1}>
                <Typography className={classes.greyText}>|</Typography>
                <Typography className={classes.greyText}>
                  {`${blacklist.items?.length ?? 0} ${t(blacklist.blacklistType === BlacklistTypes.COMPANY ? 'companies' : 'contacts')} ${t(
                    'addedToBlacklist'
                  )}`}
                </Typography>
              </Stack>
            </Stack>
            <Stack direction={'row'} alignItems={'center'} gap={2}>
              <PaiButton
                text={blacklist.blacklistType === BlacklistTypes.COMPANY ? t('addCompanies') : t('addContacts')}
                size={'small'}
                border={'round'}
                action={handleOpenEditBlacklistItemsDialog}
              />
              <IconButton className={cx(classes.expandButton, expanded ? 'expanded' : undefined)} size={'small'} onClick={handleToggleExpand}>
                <ExpandMoreIcon />
              </IconButton>
            </Stack>
          </Stack>
          <Collapse className={classes.itemCollapse} in={expanded} timeout={0} unmountOnExit={true}>
            <BlacklistItemTable
              blacklistType={blacklist.blacklistType}
              blacklistItems={blacklist.items}
              loading={loading}
              handleRemoveSingleBlacklistItem={handleRemoveSingleBlacklistItem}
            />
          </Collapse>
        </div>
        <div className={classes.blacklistItemActions}>
          <IconButton className={classes.iconButton} size={'small'} onClick={(event: React.MouseEvent) => handleOpenMenu(event.target)}>
            <EditIcon />
          </IconButton>
        </div>
      </Stack>

      <BlacklistItemMenu
        anchorEl={anchorEl}
        menuOpen={menuOpen}
        handleOpenEditBlacklistNameDialog={handleOpenEditBlacklistNameDialog}
        handleOpenDeleteBlacklistDialog={handleOpenDeleteBlacklistDialog}
        handleCloseMenu={handleCloseMenu}
      />
    </div>
  );
};

interface Props {
  blacklists: Blacklist_New[];
  loading: boolean;

  handleOpenEditBlacklistItemsDialog(blacklist: Blacklist_New): () => void;

  handleOpenEditBlacklistNameDialog(blacklist: Blacklist_New): () => void;

  handleOpenDeleteBlacklistDialog(blacklist: Blacklist_New): () => void;

  handleRemoveSingleBlacklistItem(blacklist: Blacklist_New): (identifier: string) => () => void;
}

const GlobalBlacklistsTable: React.FC<Props> = ({
  blacklists,
  loading,
  handleOpenEditBlacklistItemsDialog,
  handleOpenEditBlacklistNameDialog,
  handleOpenDeleteBlacklistDialog,
  handleRemoveSingleBlacklistItem,
}) => {
  const { classes } = useStyles();

  return (
    <Stack className={classes.blacklistTable} gap={1}>
      {blacklists.map((blacklist) => (
        <BlacklistItem
          key={blacklist._id}
          blacklist={blacklist}
          loading={loading}
          handleOpenEditBlacklistItemsDialog={handleOpenEditBlacklistItemsDialog(blacklist)}
          handleOpenEditBlacklistNameDialog={handleOpenEditBlacklistNameDialog(blacklist)}
          handleOpenDeleteBlacklistDialog={handleOpenDeleteBlacklistDialog(blacklist)}
          handleRemoveSingleBlacklistItem={handleRemoveSingleBlacklistItem(blacklist)}
        />
      ))}
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  blacklistTable: {
    overflow: 'auto',
  },
  blacklistItem: {
    width: '100%',
  },
  blacklistItemContent: {
    width: '100%',
    padding: '8px 32px',
    background: '#FFF',
    borderRadius: '12px',
  },
  blacklistItemActions: {
    flex: 1,
    height: '44px',
    padding: '8px 16px',
    background: '#FFF',
    borderRadius: '12px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  expandButton: {
    padding: '1px',
    '& svg': {
      fontSize: theme.typography.pxToRem(20),
      transition: '225ms',
    },
    '&.expanded': {
      '& svg': {
        transform: 'rotate(180deg)',
      },
    },
  },
  iconButton: {
    padding: '4px',
    '& svg': {
      fontSize: theme.typography.pxToRem(16),
    },
  },
  itemCollapse: {
    paddingTop: '16px',
  },
  regularText: {
    fontSize: theme.typography.pxToRem(15),
  },
  greyText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  linkContainer: {
    padding: '0 32px',
  },
}));

export default GlobalBlacklistsTable;
